<template>
	<v-container fluid>
		<v-card elevation="0" class="ant-card px-4 pb-2">
			<v-card-title class="pa-0">
				<v-col cols="auto" class="d-flex align-center">
					<div class="v-typography--title1">{{ $t("batches") }}</div>
				</v-col>
				<v-spacer />
				<v-col cols="auto" class="d-flex justify-end px-0">
					<v-btn text outlined class="mx-1" @click="setAllVisibility(true)">
						<v-icon size="15">icon-eye</v-icon>
						<span class="ml-1 v-typography--body-sm">{{ $t("show_all") }}</span>
					</v-btn>
					<v-btn text outlined class="mx-1" @click="setAllVisibility(false)">
						<v-icon size="15">icon-eye-crossed</v-icon>
						<span class="ml-1 v-typography--body-sm">{{ $t("hide_all") }}</span>
					</v-btn>
					<v-btn text outlined class="mx-1" @click="openNewBatch">
						<v-icon size="15">icon-plus</v-icon>
						<span class="ml-1 v-typography--body-sm">{{ $t("add_stock_batch") }}</span>
					</v-btn>
				</v-col>
			</v-card-title>

			<v-divider class="mb-4" />

			<v-row>
				<v-col cols="12">
					<common-table
						:headers="headers"
						:items="list"
						:items-length="pagination.totalItemCount"
						:options.sync="tableOptions"
						:loading="loading"
						:items-per-page="tableOptions.itemsPerPage"
						:items-per-page-options="tableFooterProps.itemsPerPageOptions"
					>
						<template #item.images="{ item }">
							<div class="d-flex align-center" style="min-height: 80px">
								<template v-if="item.imagesLoaded.length">
									<div
										v-for="(u, i) in item.imagesLoaded.slice(0, 4)"
										:key="i"
										class="img-wrapper pointer"
										@click="openGallery(item.imagesLoaded, i)"
									>
										<v-img :src="u" class="image-box" />
										<div v-if="i === 3 && item.imagesLoaded.length > 4" class="image-badge">
											+{{ item.imagesLoaded.length - 4 }}
										</div>
									</div>
								</template>
								<template v-else>
									<div class="img-wrapper placeholder-text">No Image</div>
								</template>
							</div>
						</template>

						<template #item.measurements="{ item }">
							<div v-for="(m, j) in item.measurements" :key="j" class="mb-2">
								<div v-if="m.length !== undefined">{{ $t("length") }}: {{ m.length }}</div>
								<div v-if="m.width !== undefined">{{ $t("width") }}: {{ m.width }}</div>
								<div v-if="m.height !== undefined">{{ $t("height") }}: {{ m.height }}</div>
								<div v-if="m.value !== undefined">{{ $t("value") }}: {{ m.value }}</div>
								<div>{{ $t("pieces") }}: {{ m.pieces }}</div>
							</div>
						</template>

						<template #item.quantity="{ item }">
							<span>{{ item.quantity }}{{ meta.unitCode }}</span>
						</template>

						<template #item.weight="{ item }">
							<span>{{ item.weight ? item.weight + "kg" : "-" }}</span>
						</template>

						<template #item.stockStatus="{ item }">
							<span>{{ $t(item.quantityStatus) }}</span>
						</template>

						<template #item.visibility="{ item }">
							<v-icon class="pointer" @click="toggleVisibility(item)">
								{{ item.visibility ? "icon-eye" : "icon-eye-crossed" }}
							</v-icon>
						</template>

						<template #item.actions="{ item }">
							<v-btn icon @click="startEdit(item)"><v-icon size="20">icon-edit</v-icon></v-btn>
							<v-btn icon @click="confirmDelete(item)"><v-icon size="20" color="error">icon-delete</v-icon></v-btn>
						</template>
					</common-table>
				</v-col>
			</v-row>

			<div
				v-if="showGallery"
				class="custom-overlay d-flex flex-column justify-center align-center"
				@click="closeGallery"
			>
				<v-btn icon class="overlay-close" @click.stop="closeGallery"><v-icon color="white">icon-cross</v-icon></v-btn>
				<v-btn icon class="overlay-arrow-left" @click.stop="prevImage"
					><v-icon color="white">icon-angle-left</v-icon></v-btn
				>
				<v-btn icon class="overlay-arrow-right" @click.stop="nextImage"
					><v-icon color="white">icon-angle-right</v-icon></v-btn
				>
				<v-img :src="currentGallery[currentIndex]" max-width="90%" max-height="90%" contain @click.stop />
			</div>

			<new-batch-dialog
				v-model="dialogVisible"
				:stockId="stockId"
				:companyId="meta.companyId"
				:unitCode="meta.unitCode"
				:formatId="meta.formatId"
				:measurementId="meta.measurementId"
				:formatMeta="meta.formatMeta"
				:measurementMeta="meta.measurementMeta"
				:autoComputedTotal="meta.autoComputedTotal"
				:batchData="editingBatch"
				@saved="emitRefresh"
			/>
		</v-card>
	</v-container>
</template>

<script>
import CommonTable from "../../partials/CommonTable.vue";
import NewBatchDialog from "./dialogs/NewBatchDialog.vue";
import remote from "../../../data/remote";
import { pagination as paginationMixin } from "../../mixins/utils.mixin";
import uiMixin from "../../../store/interfaces/ui.mixin";

export default {
	name: "Batches",
	components: { CommonTable, NewBatchDialog },
	mixins: [paginationMixin, uiMixin],
	props: {
		stockId: { type: String, required: true },
		meta: { type: Object, required: true },
		batches: { type: Array, required: true },
	},
	data() {
		return {
			list: [],
			loading: false,
			dialogVisible: false,
			editingBatch: null,
			showGallery: false,
			currentGallery: [],
			currentIndex: 0,
		};
	},
	computed: {
		headers() {
			return [
				{ text: this.$t("images"), value: "images" },
				{ text: this.$t("sizes"), value: "measurements" },
				{ text: `${this.$t("quantity")} (${this.meta.unitCode})`, value: "quantity" },
				{ text: this.$t("weight_kg"), value: "weight" },
				{ text: this.$t("stock_status"), value: "stockStatus" },
				{ text: this.$t("show_in_stocks"), value: "visibility" },
				{ text: this.$t("actions"), value: "actions", sortable: false },
			];
		},
	},
	watch: {
		batches: {
			handler() {
				this.prepareRows();
			},
			immediate: true,
		},
	},
	methods: {
		async prepareRows() {
			this.loading = true;
			const rows = await Promise.all(
				(this.batches || []).map(async (b) => {
					const imgs = [];
					if (Array.isArray(b.images) && b.images.length) {
						for (const k of b.images) {
							try {
								const blob = await remote.files.getBlob(k);
								imgs.push(URL.createObjectURL(blob));
							} catch {}
						}
					}
					return { ...b, id: b._id || b.id, imagesLoaded: imgs };
				})
			);
			this.list = rows;
			this.pagination = { totalItemCount: rows.length };
			this.loading = false;
		},
		toggleVisibility(row) {
			remote.stocks.updateBatch(this.stockId, { ...row, visibility: !row.visibility });
			row.visibility = !row.visibility;
			this.emitRefresh();
		},
		async setAllVisibility(v) {
			await Promise.all(
				this.list
					.filter((b) => b.visibility !== v)
					.map((b) => remote.stocks.updateBatch(this.stockId, { ...b, visibility: v }))
			);
			this.list.forEach((b) => (b.visibility = v));
			this.emitRefresh();
		},
		openNewBatch() {
			this.editingBatch = null;
			this.dialogVisible = true;
		},
		startEdit(row) {
			this.editingBatch = JSON.parse(JSON.stringify(row));
			this.dialogVisible = true;
		},
		confirmDelete(row) {
			this.showConfirmDialog({
				message: this.$t("delete_confirm_message"),
				onConfirm: () => {
					remote.stocks.deleteBatch(this.stockId, row.id, { onSuccess: this.emitRefresh });
				},
			});
		},
		openGallery(arr, i) {
			this.currentGallery = arr;
			this.currentIndex = i;
			this.showGallery = true;
		},
		closeGallery() {
			this.showGallery = false;
		},
		prevImage() {
			this.currentIndex = (this.currentIndex - 1 + this.currentGallery.length) % this.currentGallery.length;
		},
		nextImage() {
			this.currentIndex = (this.currentIndex + 1) % this.currentGallery.length;
		},
		emitRefresh() {
			this.$emit("refresh");
		},
	},
};
</script>

<style scoped>
.pointer {
	cursor: pointer;
}
.custom-overlay {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.8);
	z-index: 9999;
}
.overlay-close {
	position: absolute;
	top: 50px;
	right: 100px;
}
.overlay-arrow-left {
	position: absolute;
	top: 50%;
	left: 100px;
	transform: translateY(-50%);
}
.overlay-arrow-right {
	position: absolute;
	top: 50%;
	right: 100px;
	transform: translateY(-50%);
}
.img-wrapper {
	width: 80px;
	height: 80px;
	margin: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
}
.image-box {
	width: 100%;
	height: 100%;
	object-fit: cover;
}
.placeholder-text {
	width: 80px;
	height: 80px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #f0f0f0;
	color: #999;
	font-size: 12px;
	border: 1px dashed #ccc;
	margin: 4px;
}
.image-badge {
	position: absolute;
	bottom: 4px;
	right: 4px;
	background: rgba(0, 0, 0, 0.6);
	color: #fff;
	font-size: 12px;
	border-radius: 8px;
	padding: 1px 4px;
	line-height: 1;
}
</style>
